import React, { useState } from 'react';
import { Server, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import ClusterDetailsModal from './ClusterDetailsModal';

interface HAPairsTableProps {
  data: any;
}

const HAPairsTable: React.FC<HAPairsTableProps> = ({ data }) => {
  const haPairs = data?.ha_pairs || [];
  const [selectedCluster, setSelectedCluster] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          HA Pairs Configuration
        </h3>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Total Pairs:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {data?.total_pairs || 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">Active:</span>
            <span className="font-semibold text-green-600 dark:text-green-400">
              {data?.summary?.active_pairs || 0}
            </span>
          </div>
        </div>
      </div>

      {haPairs.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="pb-3">Cluster</th>
                <th className="pb-3">Nodes</th>
                <th className="pb-3">Status</th>
                <th className="pb-3">Usage</th>
                <th className="pb-3">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {haPairs.map((pair: any, index: number) => (
                <motion.tr
                  key={pair.cluster_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="py-3">
                    <button
                      onClick={() => setSelectedCluster({ id: pair.cluster_id, name: pair.cluster_name })}
                      className="flex items-center gap-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                    >
                      <Server className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline">
                        {pair.cluster_name}
                      </span>
                    </button>
                  </td>
                  <td className="py-3">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {pair.node_count} nodes
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {pair.nodes?.slice(0, 2).join(', ')}
                        {pair.nodes?.length > 2 && '...'}
                      </span>
                    </div>
                  </td>
                  <td className="py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      pair.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                    }`}>
                      {pair.status === 'active' ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <AlertCircle className="w-3 h-3" />
                      )}
                      {pair.status}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[100px]">
                        <div
                          className={`h-2 rounded-full ${
                            pair.usage_percent > 80 ? 'bg-red-500' :
                            pair.usage_percent > 60 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${pair.usage_percent}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {pair.usage_percent}%
                      </span>
                    </div>
                  </td>
                  <td className="py-3">
                    <span className={`text-sm ${
                      pair.is_metrocluster
                        ? 'text-blue-600 dark:text-blue-400 font-medium'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {pair.is_metrocluster ? 'MetroCluster' : 'Standard'}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Server className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p>No HA pairs configured</p>
        </div>
      )}

      {/* Cluster Details Modal */}
      {selectedCluster && (
        <ClusterDetailsModal
          clusterId={selectedCluster.id}
          clusterName={selectedCluster.name}
          onClose={() => setSelectedCluster(null)}
          data={data}
        />
      )}
    </div>
  );
};

export default HAPairsTable;